import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import Button from '@components/shared/Button';
import { useAppStore } from '@store/appStore';
import { saveSlideConfig, loadSlideConfig } from '@office/settingsService';
import { injectActivityShape, buildShapeDescriptor, getCurrentSlideIndex } from '@office/slideService';
import type { ShortAnswerConfig } from '@types-local/quiz.types';

export default function ShortAnswer(): React.ReactElement {
  const [question, setQuestion] = useState('');
  const [maxLength, setMaxLength] = useState(150);
  const [timeLimit, setTimeLimit] = useState(60);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [starValue, setStarValue] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isOfficeReady = useAppStore((s) => s.isOfficeReady);

  // Repopulate from the current slide's saved config on return.
  useEffect(() => {
    if (!isOfficeReady) return;
    let cancelled = false;
    (async () => {
      const cfg = loadSlideConfig(await getCurrentSlideIndex());
      if (cancelled || cfg?.type !== 'short_answer') return;
      setQuestion(cfg.question);
      setMaxLength(cfg.maxLength);
      setTimeLimitEnabled(cfg.timeLimit !== null);
      if (cfg.timeLimit !== null) setTimeLimit(cfg.timeLimit);
      setStarValue(cfg.starValue);
    })();
    return () => { cancelled = true; };
  }, [isOfficeReady]);

  const handleSave = async () => {
    if (!question.trim()) return;
    setSaving(true);
    try {
      const config: ShortAnswerConfig = {
        type: 'short_answer',
        question: question.trim(),
        maxLength,
        timeLimit: timeLimitEnabled ? timeLimit : null,
        starValue,
      };
      const slideIndex = await getCurrentSlideIndex();
      const shapeName = `IQ_QUIZ_SA_${slideIndex.toString().padStart(2, '0')}`;
      await saveSlideConfig(slideIndex, config, shapeName);
      await injectActivityShape(buildShapeDescriptor(config, shapeName), slideIndex);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-green-500/20 border border-green-500/30 flex items-center justify-center text-base">✏️</div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Short Answer</h2>
          <p className="text-[10px] text-slate-500">Students type a free-text response</p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Question</label>
        <textarea value={question} onChange={(e) => setQuestion(e.target.value)}
          placeholder="Describe the role of Jose Rizal in the Philippine revolution…"
          rows={3}
          className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:border-green-500 transition-colors" />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          Max Characters: <span className="text-green-300">{maxLength}</span>
        </label>
        <input type="range" min={50} max={500} step={10} value={maxLength}
          onChange={(e) => setMaxLength(Number(e.target.value))}
          className="w-full accent-green-500" />
      </div>

      <div className="space-y-2.5">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Settings</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-700">⏱ Time Limit</span>
          <div className="flex items-center gap-2">
            {timeLimitEnabled && (
              <input type="number" min={5} max={600} value={timeLimit} onChange={(e) => setTimeLimit(Number(e.target.value))}
                className="w-14 bg-surface-elevated border border-surface-border rounded-lg px-2 py-1 text-xs text-slate-900 text-center focus:outline-none" />
            )}
            <button onClick={() => setTimeLimitEnabled((v) => !v)}
              className={`w-10 h-5 rounded-full transition-all ${timeLimitEnabled ? 'bg-green-500' : 'bg-surface-elevated border border-surface-border'}`}>
              <div className={`w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform ${timeLimitEnabled ? 'translate-x-2.5' : '-translate-x-2.5'}`} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-700">⭐ Stars Award</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map((v) => (
              <button key={v} onClick={() => setStarValue(v)}
                className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${starValue === v ? 'bg-yellow-500 text-white' : 'bg-surface-elevated text-slate-500'}`}>
                {v}⭐
              </button>
            ))}
          </div>
        </div>
      </div>

      <Button fullWidth onClick={handleSave} loading={saving}
        disabled={!question.trim() || !isOfficeReady} variant={saved ? 'secondary' : 'primary'}
        icon={saved ? <Check size={14} /> : undefined}
        className={saved ? '' : 'bg-green-600 hover:bg-green-700'}>
        {saved ? 'Saved to Slide!' : 'Add to Slide'}
      </Button>
    </div>
  );
}
